/* gsap */
/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Location, Router, Link } from '@reach/router';
import Loader from 'components/Loader/Loader';
import Logo from 'components/Icons/Logo/Logo';
import NotFound from './NotFound/NotFound';
import Index from './Index/Index';
import Entry from './Entry/Entry';
import Error from './Error/Error';
import Authorize from './Authorize/Authorize';
import { useOpacityScroll } from './animations';
import useData from './data';
import useImagePreload from './imagePreload';
import './ProgramApp.scss';

/**
 * This is the ProgramApp component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function ProgramApp({
  credentials,
  listUrl,
  homeUrl,
  authorize: authorizeConfig,
  labels,
}) {
  const [data, status, message] = useData(credentials);
  const [theme, setTheme] = useState(null);
  const [view, setView] = useState('index');
  const [title, setTitle] = useState(false);
  const ref = useOpacityScroll();

  useImagePreload(data);

  useEffect(() => {
    if (data) {
      setTheme(data.theme);
    }
  }, [data]);

  useEffect(() => {
    document.body.classList.add(`theme-${theme}`);

    return () => {
      document.body.classList.remove(`theme-${theme}`);
    };
  }, [theme]);

  function renderTitle() {
    if (!title) return false;
    return <small>{data.name}</small>;
  }

  function renderContent() {
    if (status === 500) return <Error>{message}</Error>;
    if (status === 404) return <NotFound>{message}</NotFound>;
    if (!data) return <Loader key="loader" />;

    const nav = data.entries.map(({ id, nav, url }) => ({
      id,
      text: nav
        .replace('<br>', '')
        .replace('<br />', '')
        .replace('\n', '')
        .replace('  ', ' '),
      url,
    }));

    const EntryProxy = props => {
      useEffect(() => {
        setTitle(true);
        setView('entry');
      }, []);

      if (!data) return null;

      // eslint-disable-next-line
      if (!props.slug) return null;

      // eslint-disable-next-line
      const index = data.entries.findIndex(({ slug }) => slug === props.slug);
      const next = index + 1 < data.entries.length ? index + 1 : 0;

      return (
        <Entry
          key={`entry-${index}`}
          {...props}
          {...data.entries[index]}
          nav={nav}
          next={{
            ...data.entries[next],
            labels,
          }}
          labels={labels}
          onTheme={theme => {
            setTheme(theme);
          }}
        />
      );
    };

    return (
      <Location key="content">
        {({ location }) => (
          <TransitionGroup>
            <CSSTransition
              key={location.href}
              timeout={1500}
              onEnter={() => {
                document.body.classList.add('transitioning');
              }}
              onEntered={() => {
                document.body.classList.remove('transitioning');
              }}
            >
              <Router location={location}>
                <Index
                  key="index"
                  path="/:program"
                  {...data}
                  nav={nav}
                  labels={labels}
                  onMount={() => {
                    setTitle(false);
                    setView('index');
                  }}
                  onTheme={theme => {
                    setTheme(theme);
                  }}
                />
                <EntryProxy path="/:program/:slug" />
                <NotFound default />
              </Router>
            </CSSTransition>
          </TransitionGroup>
        )}
      </Location>
    );
  }

  function renderLogin() {
    if (!data) return null;
    if (!data.locked) return null;
    if (data.purchased) return null;

    return (
      <Authorize
        key="authorize"
        {...authorizeConfig}
        sku={data.sku}
        name={data.name}
        price={data.price}
        basket={data.basket}
      />
    );
  }

  function renderHomeLink() {
    if (view === 'index') {
      return (
        <a ref={ref} className="logo" href={listUrl}>
          <Logo />
          {renderTitle()}
        </a>
      );
    }

    return (
      <Link ref={ref} className="logo" to={homeUrl}>
        <Logo />
        {renderTitle()}
      </Link>
    );
  }

  return (
    <>
      {renderHomeLink()}
      {renderContent()}
      {renderLogin()}
    </>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
ProgramApp.propTypes = {
  credentials: PropTypes.shape({
    clientAction: PropTypes.string.isRequired,
    loginAction: PropTypes.string.isRequired,
    dataAction: PropTypes.string.isRequired,
    defaultClient: PropTypes.object,
  }).isRequired,
  listUrl: PropTypes.string.isRequired,
  homeUrl: PropTypes.string.isRequired,
  authorize: PropTypes.object.isRequired,
  labels: PropTypes.shape({
    name: PropTypes.string.isRequired,
    next: PropTypes.string.isRequired,
  }).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
ProgramApp.defaultProps = {
  listUrl: '/',
  homeUrl: '/',
  authorize: {},
  labels: {
    name: 'Digital program',
    next: 'Next story',
  },
};

export default ProgramApp;
